/**
 * Agent Manager Modal
 *
 * Provides a modal interface for browsing and selecting agents.
 * Follows the same pattern as model-manager.js.
 */

import { state } from './state.js';
import { escapeHtml } from './html-escape.js';
import { getHeaders } from './api.js';

let agents = [];
let selectedAgentName = null;
let modalOpen = false;

const modalElements = {
    overlay: null,
    panel: null,
    closeBtn: null,
    searchInput: null,
    agentList: null,
    clearBtn: null,
    selectBtn: null
};

// Callback for when agent is selected
let onAgentSelected = null;

/**
 * Initialize the Agent Manager
 * @param {Function} callback - Called with (agentName, agentData) when agent is selected
 */
export function initAgentManager(callback) {
    onAgentSelected = callback;
    createModalHTML();
    cacheElements();
    setupEventListeners();
}

/**
 * Create the modal HTML structure
 */
function createModalHTML() {
    const modalHTML = `
        <div id="agent-manager-overlay" class="modal-overlay"></div>
        <div id="agent-manager-panel" class="context-manager-panel">
            <div class="context-manager-header">
                <h2>Select Agent</h2>
                <button id="agent-manager-close" class="modal-close">&times;</button>
            </div>
            <div class="context-manager-search">
                <input type="text" id="agent-search" placeholder="Search agents...">
            </div>
            <div id="agent-list" class="context-manager-list">
                <div class="context-manager-empty">Loading agents...</div>
            </div>
            <div class="context-manager-footer">
                <button id="agent-clear-btn" class="btn-secondary">Clear</button>
                <button id="agent-select-btn" class="menu-btn primary" disabled>Select Agent</button>
            </div>
        </div>
    `;
    document.body.insertAdjacentHTML('beforeend', modalHTML);
}

/**
 * Cache DOM elements
 */
function cacheElements() {
    modalElements.overlay = document.getElementById('agent-manager-overlay');
    modalElements.panel = document.getElementById('agent-manager-panel');
    modalElements.closeBtn = document.getElementById('agent-manager-close');
    modalElements.searchInput = document.getElementById('agent-search');
    modalElements.agentList = document.getElementById('agent-list');
    modalElements.clearBtn = document.getElementById('agent-clear-btn');
    modalElements.selectBtn = document.getElementById('agent-select-btn');
}

/**
 * Setup event listeners
 */
function setupEventListeners() {
    modalElements.overlay?.addEventListener('click', closeAgentManager);
    modalElements.closeBtn?.addEventListener('click', closeAgentManager);
    modalElements.searchInput?.addEventListener('input', handleSearch);
    modalElements.clearBtn?.addEventListener('click', handleClear);
    modalElements.selectBtn?.addEventListener('click', handleSelect);

    // Close on escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modalOpen) {
            closeAgentManager();
        }
    });
}

/**
 * Open the Agent Manager modal
 */
export async function openAgentManager() {
    modalOpen = true;
    selectedAgentName = null;

    // Reset search
    if (modalElements.searchInput) {
        modalElements.searchInput.value = '';
    }

    // Show modal
    modalElements.overlay?.classList.add('active');
    modalElements.panel?.classList.add('open');
    document.body.style.overflow = 'hidden';

    // Fetch and render agents
    await fetchAgents();
    renderAgentList();

    // Update button states
    updateButtonStates();
}

/**
 * Close the Agent Manager modal
 */
export function closeAgentManager() {
    modalOpen = false;
    modalElements.overlay?.classList.remove('active');
    modalElements.panel?.classList.remove('open');
    document.body.style.overflow = '';
}

/**
 * Fetch agents from API
 */
async function fetchAgents() {
    try {
        const response = await fetch('/agents', { headers: getHeaders(false) });
        if (!response.ok) throw new Error('Failed to fetch agents');
        const data = await response.json();
        agents = data.agents || [];
    } catch (error) {
        console.error('Error fetching agents:', error);
        agents = [];
    }
}

/**
 * Render the agent list
 */
function renderAgentList() {
    if (!modalElements.agentList) return;

    const searchTerm = modalElements.searchInput?.value.toLowerCase() || '';

    // Filter agents by search term
    const filteredAgents = agents.filter(agent => {
        const name = agent.name?.toLowerCase() || '';
        const desc = agent.description?.toLowerCase() || '';
        return name.includes(searchTerm) || desc.includes(searchTerm);
    });

    if (filteredAgents.length === 0) {
        modalElements.agentList.innerHTML = `
            <div class="context-manager-empty">
                ${agents.length === 0 ? 'No agents available' : 'No agents match your search'}
            </div>
        `;
        return;
    }

    // Sort: system agents first, then alphabetically
    filteredAgents.sort((a, b) => {
        const sysA = a.is_system_agent ? 0 : 1;
        const sysB = b.is_system_agent ? 0 : 1;
        if (sysA !== sysB) return sysA - sysB;
        const nameA = a.name.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        const nameB = b.name.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        return nameA.localeCompare(nameB);
    });

    let html = '<div class="context-card-grid">';
    for (const agent of filteredAgents) {
        const isSelected = agent.name === selectedAgentName;
        const isCurrent = agent.name === state.loadedAgent;

        // Format display name (convert kebab-case to Title Case)
        const displayName = agent.name
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

        html += `
            <div class="context-card ${isSelected ? 'selected' : ''} ${isCurrent ? 'current' : ''}"
                 data-agent-name="${escapeHtml(agent.name)}">
                <div class="context-card-header">
                    <span class="context-card-name">${escapeHtml(displayName)}</span>
                    <div class="context-card-badges">
                        ${agent.is_system_agent ? '<span class="badge system">SYSTEM</span>' : ''}
                        ${agent.is_light ? '<span class="badge free">LIGHT</span>' : ''}
                        ${isCurrent ? '<span class="badge current">LOADED</span>' : ''}
                    </div>
                </div>
                ${agent.description ? `<div class="context-card-description">${escapeHtml(agent.description)}</div>` : ''}
                ${renderAgentChips(agent)}
            </div>
        `;
    }
    html += '</div>';

    modalElements.agentList.innerHTML = html;

    // Add click handlers to cards
    modalElements.agentList.querySelectorAll('.context-card').forEach(card => {
        card.addEventListener('click', () => {
            const agentName = card.dataset.agentName;
            selectAgent(agentName);
        });
    });
}

/**
 * Select an agent (highlight in list)
 */
function selectAgent(agentName) {
    selectedAgentName = agentName;

    // Update visual selection
    modalElements.agentList?.querySelectorAll('.context-card').forEach(card => {
        card.classList.toggle('selected', card.dataset.agentName === agentName);
    });

    updateButtonStates();
}

/**
 * Handle search input
 */
function handleSearch() {
    renderAgentList();
}

/**
 * Handle clear button click
 */
function handleClear() {
    selectedAgentName = null;
    if (onAgentSelected) {
        onAgentSelected(null, null);
    }
    closeAgentManager();
}

/**
 * Handle select button click
 */
function handleSelect() {
    if (!selectedAgentName) return;

    const agent = agents.find(a => a.name === selectedAgentName);
    if (onAgentSelected) {
        onAgentSelected(selectedAgentName, agent);
    }
    closeAgentManager();
}

/**
 * Update button states
 */
function updateButtonStates() {
    if (modalElements.selectBtn) {
        modalElements.selectBtn.disabled = !selectedAgentName;
    }
    if (modalElements.clearBtn) {
        // Enable clear if there's a currently loaded or selected agent
        modalElements.clearBtn.disabled = !state.loadedAgent && !state.currentAgent;
    }
}

const TOOL_DISPLAY_NAMES = {
    calculator: 'Calc', datetime: 'DateTime', python_exec: 'Python',
    shell_exec: 'Shell', file_ops: 'Files', web_fetch: 'Web Fetch',
    http_request: 'HTTP', email_send: 'Email', web_search: 'Web Search', rag_search: 'RAG Search', agent_db: 'Memory',
    doc_create: 'Doc Create', vision: 'Vision', text_cleanup: 'Proofread', kb_embed: 'KB Embed'
};

function renderAgentChips(agent) {
    const chips = [];
    const kbs = agent.kbs || [];
    const tools = agent.tools || [];

    for (const kb of kbs) {
        const name = kb.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        chips.push(`<span class="context-card-chip kb" title="${escapeHtml(kb)}">${escapeHtml(name)}</span>`);
    }
    for (const tool of tools) {
        const name = TOOL_DISPLAY_NAMES[tool] || tool;
        chips.push(`<span class="context-card-chip tool">${escapeHtml(name)}</span>`);
    }
    return chips.length ? `<div class="context-card-chips">${chips.join('')}</div>` : '';
}

/**
 * Escape HTML to prevent XSS
 */

